import type { ErrorCode, ProblemDetails, ProblemFieldError } from '@apiary/shared'
import { ERROR_CODES, isProblemDetails, problemDetails } from '@apiary/shared'

/**
 * Ошибки клиента. Фронт ветвится **только** по `problem.code` (docs/06 §1),
 * никогда по HTTP-статусу и никогда по тексту.
 */

export class ApiError extends Error {
  readonly problem: ProblemDetails

  constructor(problem: ProblemDetails) {
    super(`${problem.code}: ${problem.title}`)
    this.name = 'ApiError'
    this.problem = problem
  }

  get code(): ErrorCode {
    return this.problem.code
  }

  get status(): number {
    return this.problem.status
  }

  get fieldErrors(): ProblemFieldError[] {
    return this.problem.errors ?? []
  }

  /** Значение из `meta` по ключу — для `LIMIT_EXCEEDED`, `UNSUPPORTED_MC_VERSION`. */
  meta(key: string): unknown {
    return this.problem.meta?.[key]
  }
}

/** Сеть/CORS/abort — ответа не было вообще, `problem+json` тут не бывает. */
export class NetworkError extends Error {
  constructor(message: string, cause?: unknown) {
    super(message, cause === undefined ? undefined : { cause })
    this.name = 'NetworkError'
  }
}

/**
 * Статус → код каталога, когда сервер (или прокси) ответил не нашим форматом.
 * Нужен, чтобы UI всё равно имел стабильный `code` для ветвления.
 */
export function fallbackCodeForStatus(status: number): ErrorCode {
  switch (status) {
    case 400:
      return 'VALIDATION_FAILED'
    case 401:
      return 'UNAUTHORIZED'
    case 403:
      return 'FORBIDDEN_ROLE'
    case 404:
      return 'NOT_FOUND'
    case 409:
      return 'BOT_ALREADY_IN_STATE'
    case 422:
      return 'EGRESS_BLOCKED'
    case 429:
      return 'RATE_LIMITED'
    default:
      return 'RUNNER_UNAVAILABLE'
  }
}

/** Известен ли код каталогу — защита от «нового кода» в старом клиенте. */
export function isKnownErrorCode(value: unknown): value is ErrorCode {
  return typeof value === 'string' && (ERROR_CODES as readonly string[]).includes(value)
}

/**
 * Тело ответа → `ApiError`. Если пришёл валидный `problem+json` — используем как
 * есть; иначе синтезируем problem по статусу, сохранив исходный текст в `detail`.
 */
export function toApiError(status: number, body: unknown, instance?: string): ApiError {
  if (isProblemDetails(body)) return new ApiError(body)

  const detail = extractDetail(body)
  return new ApiError(
    problemDetails(fallbackCodeForStatus(status), {
      status,
      ...(detail === undefined ? {} : { detail }),
      ...(instance === undefined ? {} : { instance })
    })
  )
}

function extractDetail(body: unknown): string | undefined {
  if (typeof body === 'string' && body.trim() !== '') return body.slice(0, 300)
  if (body !== null && typeof body === 'object') {
    const record = body as Record<string, unknown>
    for (const key of ['detail', 'message', 'error'] as const) {
      const value = record[key]
      if (typeof value === 'string' && value.trim() !== '') return value.slice(0, 300)
    }
  }
  return undefined
}
